import { useEffect, useState, type ReactNode } from "react";

import { cn } from "@/lib/utils";

const DEFAULT_STATUS_ROTATION_INTERVAL_MS = 3_200;

export function AssistantInlineStatus({
  label,
  details,
  className,
  intervalMs = DEFAULT_STATUS_ROTATION_INTERVAL_MS,
}: {
  label?: ReactNode;
  details: readonly ReactNode[];
  className?: string;
  intervalMs?: number;
}) {
  const [detailIndex, setDetailIndex] = useState(0);
  const detailCount = details.length;

  useEffect(() => {
    setDetailIndex(0);
  }, [detailCount]);

  useEffect(() => {
    if (detailCount <= 1) {
      return;
    }

    const interval = window.setInterval(() => {
      setDetailIndex((currentIndex) => (currentIndex + 1) % detailCount);
    }, intervalMs);

    return () => window.clearInterval(interval);
  }, [detailCount, intervalMs]);

  const detail = details[detailIndex % detailCount] ?? details[0];
  const hasLabel = Boolean(label);

  if (!detail) {
    return null;
  }

  return (
    <div
      className={cn(
        "inline-flex max-w-full items-center gap-2.5 text-sm leading-6 text-muted-foreground motion-safe:animate-[assistant-memory-fade-in_220ms_ease-out]",
        className,
      )}
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      {hasLabel ? (
        <>
          <span className="shrink-0 font-semibold text-foreground/58">{label}</span>
          <span className="text-muted-foreground/36" aria-hidden="true">
            /
          </span>
        </>
      ) : null}
      <span className="min-w-0 truncate bg-[linear-gradient(90deg,var(--muted-foreground)_0%,var(--muted-foreground)_34%,var(--primary)_50%,var(--muted-foreground)_66%,var(--muted-foreground)_100%)] bg-[length:220%_100%] bg-clip-text font-medium text-transparent motion-safe:animate-[assistant-status-aurora_2.35s_linear_infinite]">
        {detail}
      </span>
    </div>
  );
}
